import {
  WSAlert,
  WSAlertAction,
  WSAlertProps,
  WSElement,
  WSFlexBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  PayeeEngagementType,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";

import { selectorWorkLogConvertedResourceLabels } from "../selectors/selectorWorkLogConvertedResourceLabels";
import { selectorWorkLogPayPeriodLabel } from "../selectors/selectorWorkLogPayPeriodLabel";
import { isStatusDiscarded } from "../utils/workLogPermissions";

type AlertConfig = Pick<WSAlertProps, "theme" | "icon" | "title"> & {
  message: React.ReactNode;
  action?: WSAlertAction;
};

type Props = {
  workLog: WorkLog | undefined;
  onViewHistory?: () => void;
  /**
   * Opens the pay statement / invoice the work log converted into. Owned by the
   * route, which already resolves the right destination per resource type.
   */
  onViewConvertedResource?: () => void;
  clientName?: string;
  /**
   * Decides whether an unapproved work log is described as becoming a pay
   * statement or an invoice — `convertedResourceType` is only set after
   * approval, so before that the engagement is the only signal.
   */
  engagementType?: PayeeEngagementType | null;
  /** The converted pay statement has been paid; nothing about it can change. */
  isConvertedResourcePaid?: boolean;
};

const InlineComment: React.FC<{
  label: string;
  comment: string;
  date?: Date | string;
}> = ({ label, comment, date }) => (
  <WSElement mt="M" colorBackground="white" p="M" style={{ borderRadius: 2 }}>
    <WSFlexBox.CenterY wrap="wrap" gap="S">
      <WSText.ParagraphXs color="gray500">{label}</WSText.ParagraphXs>
      {date && (
        <>
          <WSElement
            colorBackground="gray400"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0
            }}
          />
          <WSText.ParagraphXs color="gray500">
            {toWSDateString(date, "monthDayYear")}
          </WSText.ParagraphXs>
        </>
      )}
    </WSFlexBox.CenterY>
    <WSText.ParagraphXs color="gray600" py="S">
      &ldquo;{comment}&rdquo;
    </WSText.ParagraphXs>
  </WSElement>
);

export const AlertWorkLogReviewStatusPayee: React.FC<Props> = ({
  workLog,
  onViewHistory,
  onViewConvertedResource,
  clientName,
  engagementType,
  isConvertedResourcePaid
}) => {
  const payerComment = workLog?.payerOwnedData?.comment ?? undefined;
  const payeeComment = workLog?.payeeOwnedData?.comment ?? undefined;

  // TODO(v3): comment timestamp now read from owned-data `commentAt` (v1 used the
  // work log's top-level `updatedAt`, which no longer exists).
  const commentDate =
    (payerComment
      ? workLog?.payerOwnedData?.commentAt
      : workLog?.payeeOwnedData?.commentAt) ?? undefined;

  // Only one comment exists at a time
  const comment = payerComment || payeeComment;
  const commentLabel = payerComment
    ? `Comment from ${clientName || "client"}`
    : "Your comment";

  // A W-2 work log converts into a pay statement, a 1099 one into an invoice.
  const convertedResourceLabels = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog?.convertedResourceType,
    engagementType
  });

  const alertConfigs = useMemo((): AlertConfig[] => {
    const configs: AlertConfig[] = [];

    switch (workLog?.status) {
      case WorkLogStatus.DRAFT: {
        // A draft is auto-submitted when its pay period closes — it has to be
        // actioned by then or the payer never sees it. `periodEnd` is the only
        // auto-submit date v3 exposes, and it is exactly that deadline.
        const periodEnd = workLog.periodEnd;

        if (!periodEnd) {
          break;
        }

        const payPeriodLabel = selectorWorkLogPayPeriodLabel(workLog);

        configs.push({
          theme: "info",
          icon: "info-circle",
          title: "Auto-submit coming up",
          message: `Your draft work log${
            payPeriodLabel ? ` for the pay period ${payPeriodLabel}` : ""
          } will be auto-submitted on ${toWSDateString(
            periodEnd,
            "monthDayYear"
          )}. Finish filling it out before then so you're paid the correct amount this period.`
        });
        break;
      }
      case WorkLogStatus.APPROVED:
        configs.push({
          theme: "success",
          icon: "check-circle",
          title: isConvertedResourcePaid
            ? "Work log paid"
            : "Work log approved",
          message: (
            <>
              {`Work log was approved and a ${convertedResourceLabels.noun} was generated.`}
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          ),
          action:
            workLog.convertedResourceId && onViewConvertedResource
              ? {
                  text: convertedResourceLabels.view,
                  onClick: onViewConvertedResource
                }
              : undefined
        });
        break;
      case WorkLogStatus.IN_REVIEW:
        if (payeeComment) {
          configs.push({
            theme: "neutral",
            icon: "time",
            title: "Awaiting client response",
            message: (
              <>
                You have requested that the submitted work log be returned for
                editing. The client can respond to the request by returning the
                work log back to you for corrections, make edits themselves, or
                approve it as-is. You&apos;ll be notified of their decision.
                <InlineComment
                  label={commentLabel}
                  comment={payeeComment}
                  date={commentDate}
                />
              </>
            ),
            // TODO(v3-blocker): no endpoint clears the payee comment
            // (`WorkLogUpdate` carries only notes/attachments and the transitions
            // take no comment), so a "Cancel request for editing" action could
            // only ever pretend to work — the client would still see the
            // request. Restore it once v3 can unset `payeeOwnedData.comment`.
            action: undefined
          });
        } else {
          configs.push({
            theme: "neutral",
            icon: "info-circle",
            title: "Awaiting client approval",
            message: (
              <>
                Your client will review and either approve this work log or
                request changes. You&apos;ll be notified of their decision. Work
                logs cannot be edited while in review. Need to make corrections?
                Use{" "}
                <WSText.ParagraphSm inline weight="medium">
                  &quot;Request editing&quot;
                </WSText.ParagraphSm>{" "}
                above to ask the client to send the work log back to you for
                editing.
              </>
            )
          });
        }
        break;
      case WorkLogStatus.IN_PROGRESS:
        configs.push({
          theme: "warning",
          icon: "alert",
          title: "Action required",
          message: (
            <>
              The work log was returned to you for editing. Once the work log is
              ready, resubmit it for client approval.
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          )
        });
        break;
      case WorkLogStatus.REJECTED:
        configs.push({
          theme: "error",
          icon: "block",
          title: "Rejected",
          message: (
            <>
              {`The work log was rejected and no ${convertedResourceLabels.noun} was created.`}
              {comment && (
                <InlineComment
                  label={commentLabel}
                  comment={comment}
                  date={commentDate}
                />
              )}
            </>
          ),
          action: onViewHistory
            ? {
                text: "View history",
                onClick: onViewHistory
              }
            : undefined
        });
        break;
    }

    // Handle "Discarded" status (not in the SDK WorkLogStatus enum yet).
    // Neutral, not an error: an empty auto-submitted work log being discarded is
    // the expected outcome, not something that failed.
    if (isStatusDiscarded(workLog?.status as string)) {
      configs.push({
        theme: "neutral",
        icon: "info-circle",
        title: "Discarded",
        message:
          "This work log was empty when it was auto-submitted, so it was invalid and has been discarded. Your client can re-open it if you still need to log work for this period.",
        action: onViewHistory
          ? {
              text: "View history",
              onClick: onViewHistory
            }
          : undefined
      });
    }

    return configs;
  }, [
    workLog,
    comment,
    commentDate,
    commentLabel,
    payeeComment,
    convertedResourceLabels.noun,
    convertedResourceLabels.view,
    isConvertedResourcePaid,
    onViewConvertedResource,
    onViewHistory
  ]);

  if (alertConfigs.length === 0) {
    return null;
  }

  return (
    <WSList gap="M">
      {alertConfigs.map(({ message, ...alertProps }, index) => (
        <WSAlert key={index} {...alertProps}>
          {message}
        </WSAlert>
      ))}
    </WSList>
  );
};
